import { Tooltip, TooltipContent, TooltipTrigger } from '@design-system';
import { CareEnvironment } from '@shared/appModel';
import { DAYS_IN_MONTH } from '@shared/calculations/funding/projection/sharedConstants';
import { BalanceBreakdownTooltip } from '../BalanceBreakdownTooltip';
import { appliedCapTooltipInputs, InflationTooltipContent } from '../cells';
import { currency } from '../formatters';
import { PROJECTED_COST_NOTE } from '../columnGroups';
import { GrowthFactor, HeaderTooltip } from '../HeaderTooltip';
import { AccumulationWhenCell, GrowthValueCell } from '../../accumulationTimeline/cells';
import type { AccumulationColumnSpec, AccumulationCtx } from '../../accumulationTimeline/types';
import { indemnityPayoutWaterfall } from './indemnityPayout';
import { reimbursementPayoutRows } from './payoutBreakdown';
import type { StrategyPresentation } from './types';

type MoneyGuardDeltas = {
  indemnitySurplusApplied?: number;
};

// Lincoln MoneyGuard Fixed Advantage is structurally identical to OneAmerica
// Asset Care: a two-pool reimbursement hybrid drawn Face-first, then
// Continuation of Benefits (COB). The presentation mirrors oaAssetCarePresentation
// but uses MoneyGuard's product wording — the base pool is the Face / Specified
// Amount (not "Acceleration of Benefits"), and indemnity cash is capped at the
// policy's `policyIndemnityBenefitPercent` of the Monthly Maximum (the upload
// field map fixes this at 80% for MoneyGuard, vs Asset Care's 75%; hand-entered
// policies default to 100%). See lincolnMoneyGuardStrategy.ts.

const moneyGuardFacePool = (snapshot: YearSnapshot): number =>
  snapshot.theoreticalLtcPoolCeiling - snapshot.effectiveContinuationBenefit;

const moneyGuardCobPool = (snapshot: YearSnapshot): number => snapshot.effectiveContinuationBenefit;

function buildMoneyGuardInflationTooltip({
  row,
  startingLabel,
  startingValue,
  rate,
  capYears,
  result,
  compoundLabel,
}: {
  row: AccumulationYear;
  startingLabel: string;
  startingValue: number;
  rate: number;
  capYears: number;
  result: number;
  compoundLabel: string;
}) {
  const intYear = Math.floor(row.policyYear);
  const cap = capYears > 0 ? capYears : null;
  const rawIncrements = Math.max(intYear - 1, 0);
  const increments = cap !== null ? Math.min(rawIncrements, cap) : rawIncrements;
  const capped = cap !== null && intYear > cap;

  return (
    <InflationTooltipContent
      header={`Policy year ${intYear}`}
      startingLabel={startingLabel}
      startingValue={startingValue}
      rate={rate}
      years={increments}
      result={result}
      note={capped ? `Inflation capped at year ${cap}` : undefined}
      compoundLabel={compoundLabel}
    />
  );
}

function MoneyGuardFlatValueTooltip({ row, value }: { row: AccumulationYear; value: number }) {
  return (
    <TooltipContent className="text-xs">
      <div className="mb-1 font-semibold">Policy year {Math.floor(row.policyYear)}</div>
      <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
        <span className="text-gray-300">Death benefit</span>
        <span className="font-semibold tabular-nums">{currency(value)}</span>
        <span className="text-gray-300">Driven by</span>
        <span className="font-semibold">Flat Face Amount before care begins</span>
      </div>
    </TooltipContent>
  );
}

function buildLincolnMoneyGuardAccumulationColumns({
  terms,
  timeline,
}: AccumulationCtx): AccumulationColumnSpec[] {
  const faceRate = terms.baseBenefitPoolInflationRate ?? terms.inflationRate;
  const faceCapYears = terms.baseBenefitPoolInflationCapYears ?? terms.inflationCapYears;
  const cobRate = terms.extensionOfBenefitsPoolInflationRate ?? terms.inflationRate;
  const cobCapYears = terms.extensionOfBenefitsPoolInflationCapYears ?? terms.inflationCapYears;
  const firstSnapshot = timeline[0]?.snapshot;
  const faceAtIssue =
    terms.baseBenefitPoolAtIssue ?? (firstSnapshot ? moneyGuardFacePool(firstSnapshot) : 0);
  const cobAtIssue =
    terms.extensionOfBenefitsPoolAtIssue ?? (firstSnapshot ? moneyGuardCobPool(firstSnapshot) : 0);
  const monthlyBenefitAtIssue =
    terms.nominalDailyBenefitBase * DAYS_IN_MONTH || firstSnapshot?.monthlyBenefitCap || 0;

  const columns: AccumulationColumnSpec[] = [
    {
      key: 'when',
      label: 'Policy year',
      sticky: true,
      render: row => <AccumulationWhenCell row={row} />,
    },
  ];

  if (timeline.some(row => moneyGuardFacePool(row.snapshot) > 0.5)) {
    columns.push({
      key: 'face-pool',
      label: 'Face Amount',
      tooltip: (
        <HeaderTooltip
          description="Face Amount available for long-term-care acceleration before benefits are used. It is the first pool MoneyGuard draws from once care begins."
          formulas={[
            {
              label: 'Face Amount',
              expression: (
                <>
                  Face Amount at issue ×{' '}
                  <GrowthFactor
                    rate="Face inflation rate"
                    exponent="policy year − 1"
                  />
                </>
              ),
            },
          ]}
          notes="If a Face inflation rate is not set, the policy inflation rate is used. The policy inflation cap year applies when set."
        />
      ),
      render: (row, prev) => (
        <GrowthValueCell
          value={moneyGuardFacePool(row.snapshot)}
          prevValue={prev ? moneyGuardFacePool(prev.snapshot) : null}
          tooltip={buildMoneyGuardInflationTooltip({
            row,
            startingLabel: `Face Amount at issue (${terms.policyPremiumStartYear})`,
            startingValue: faceAtIssue,
            rate: faceRate,
            capYears: faceCapYears,
            result: moneyGuardFacePool(row.snapshot),
            compoundLabel: 'Face Amount inflation',
          })}
        />
      ),
    });
  }

  if (timeline.some(row => moneyGuardCobPool(row.snapshot) > 0.5)) {
    columns.push({
      key: 'cob-pool',
      label: 'COB',
      tooltip: (
        <HeaderTooltip
          description="Continuation of Benefits available after the Face Amount is exhausted. It grows separately from the Face Amount when a COB inflation rate is set."
          formulas={[
            {
              label: 'COB',
              expression: (
                <>
                  COB at issue ×{' '}
                  <GrowthFactor
                    rate="COB inflation rate"
                    exponent="policy year − 1"
                  />
                </>
              ),
            },
          ]}
          notes="If a COB inflation rate is not set, the policy inflation rate is used. The policy inflation cap year applies when set."
        />
      ),
      render: (row, prev) => (
        <GrowthValueCell
          value={moneyGuardCobPool(row.snapshot)}
          prevValue={prev ? moneyGuardCobPool(prev.snapshot) : null}
          tooltip={buildMoneyGuardInflationTooltip({
            row,
            startingLabel: `COB at issue (${terms.policyPremiumStartYear})`,
            startingValue: cobAtIssue,
            rate: cobRate,
            capYears: cobCapYears,
            result: moneyGuardCobPool(row.snapshot),
            compoundLabel: 'COB inflation',
          })}
        />
      ),
    });
  }

  if (timeline.some(row => row.snapshot.monthlyBenefitCap > 0.5)) {
    columns.push({
      key: 'monthly-benefit',
      label: 'Monthly benefit',
      tooltip: (
        <HeaderTooltip
          description="Maximum monthly LTC benefit before care begins. MoneyGuard uses the highest configured daily benefit, then applies the Face Amount inflation rate."
          formulas={[
            {
              label: 'Monthly benefit',
              expression: (
                <>
                  highest daily benefit × {DAYS_IN_MONTH} ×{' '}
                  <GrowthFactor
                    rate="Face inflation rate"
                    exponent="policy year − 1"
                  />
                </>
              ),
            },
          ]}
          notes="Monthly claim rows use the daily benefit for that care setting; this before-care table shows the policy maximum."
        />
      ),
      render: (row, prev) => (
        <GrowthValueCell
          value={row.snapshot.monthlyBenefitCap}
          prevValue={prev?.snapshot.monthlyBenefitCap ?? null}
          tooltip={buildMoneyGuardInflationTooltip({
            row,
            startingLabel: `Monthly benefit at issue (${terms.policyPremiumStartYear})`,
            startingValue: monthlyBenefitAtIssue,
            rate: faceRate,
            capYears: faceCapYears,
            result: row.snapshot.monthlyBenefitCap,
            compoundLabel: 'Face Amount inflation',
          })}
        />
      ),
    });
  }

  if (timeline.some(row => row.snapshot.effectiveFaceAmount > 0.5)) {
    columns.push({
      key: 'death-benefit',
      label: 'Death benefit',
      tooltip: (
        <HeaderTooltip
          description="Death benefit before long-term-care benefits are used. For MoneyGuard this stays flat at the Face Amount."
          formulas={[{ label: 'Death benefit', expression: 'Face Amount at issue' }]}
          notes="After care begins, only Face Amount drawdown can reduce it; COB drawdown does not."
        />
      ),
      render: (row, prev) => (
        <GrowthValueCell
          value={row.snapshot.effectiveFaceAmount}
          prevValue={prev?.snapshot.effectiveFaceAmount ?? null}
          tooltip={
            <MoneyGuardFlatValueTooltip
              row={row}
              value={row.snapshot.effectiveFaceAmount}
            />
          }
        />
      ),
    });
  }

  return columns;
}

export const lincolnMoneyGuardPresentation: StrategyPresentation = {
  accumulationColumns: buildLincolnMoneyGuardAccumulationColumns,

  // MoneyGuard's monthly cap is per care environment and inflates on the
  // Face/base rate (not the policy-wide rate the default BenefitAmountCell
  // uses), so the cell headlines the engine's own applied cap; the tooltip's
  // base × (1+r)^n derivation is reconstructed from terms for display only.
  benefitAmountCell: ({ month, terms }) => {
    // Pre-policy rows carry no applied cap; fall through to the default cell
    // (which renders a dash when there's no snapshot for the year).
    if (month.events.ineligibilityReason === 'pre-policy') return null;
    const monthlyCap = month.facts.appliedMonthlyBenefitCap;
    const { baseMonthly, rate, capYears } = appliedCapTooltipInputs(month, terms);
    const intYear = Math.floor(month.context.policyYear);
    const cap = capYears && capYears > 0 ? capYears : null;
    // Exponent = policyYear − 1, capped at capYears, matching the engine's
    // `compoundFactor` so the tooltip's base × (1+r)^n equals the displayed result.
    const rawIncrements = Math.max(intYear - 1, 0);
    const increments = cap !== null ? Math.min(rawIncrements, cap) : rawIncrements;
    const capped = cap !== null && intYear > cap;
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="cursor-help leading-tight">
            <div className="font-medium text-gray-900">{currency(monthlyCap)}</div>
            <div className="text-[10px] text-gray-500">
              {currency(monthlyCap / DAYS_IN_MONTH)}/day
            </div>
          </div>
        </TooltipTrigger>
        <InflationTooltipContent
          header={`Policy year ${intYear}`}
          startingLabel="At issue"
          startingValue={baseMonthly}
          rate={rate}
          years={increments}
          result={monthlyCap}
          note={capped ? `Inflation capped at year ${cap}` : undefined}
          compoundLabel="Compound growth"
        />
      </Tooltip>
    );
  },

  benefitAmountValueForMonth: ({ month }) =>
    month.events.ineligibilityReason === 'pre-policy'
      ? undefined
      : month.facts.appliedMonthlyBenefitCap,

  poolCellTooltip: ({ month, openState }) => {
    const openBase = openState.pools.baseBenefitPool;
    const openEob = openState.pools.extensionOfBenefitsPool;
    const closeBase = month.state.pools.baseBenefitPool;
    const closeEob = month.state.pools.extensionOfBenefitsPool;
    const baseDepletion = month.deltas.base.depletion;
    const eobDepletion = month.deltas.eob.depletion;
    const baseAccrual = month.deltas.base.inflationAccrual;
    const eobAccrual = month.deltas.eob.inflationAccrual;

    const allZero =
      openBase === 0 &&
      openEob === 0 &&
      closeBase === 0 &&
      closeEob === 0 &&
      baseDepletion === 0 &&
      eobDepletion === 0 &&
      baseAccrual === 0 &&
      eobAccrual === 0;
    if (allZero) return null;

    return (
      <BalanceBreakdownTooltip
        title="LTC Pool Breakdown"
        description="MoneyGuard pays from the Face Amount (LTC acceleration) first, then Continuation of Benefits (COB). Inflation accrues at policy anniversaries while each pool remains open."
        sections={[
          {
            heading: 'Face Amount (LTC acceleration)',
            opening: openBase,
            closing: closeBase,
            lines: [
              { label: 'Inflation accrual', value: baseAccrual, kind: 'add' },
              { label: 'Drawn this month', value: baseDepletion, kind: 'subtract' },
            ],
          },
          {
            heading: 'Continuation of Benefits (COB)',
            opening: openEob,
            closing: closeEob,
            lines: [
              { label: 'Inflation accrual', value: eobAccrual, kind: 'add' },
              { label: 'Drawn this month', value: eobDepletion, kind: 'subtract' },
            ],
          },
        ]}
      />
    );
  },

  deathBenefitCellTooltip: ({ month, openState }) => {
    const open = openState.pools.deathBenefit;
    const close = month.state.pools.deathBenefit;
    const reduction = month.deltas.deathBenefit.reduction;
    const flooredAtMinimum = month.deltas.deathBenefit.flooredAtMinimum;
    const baseDrawdown = month.deltas.base.depletion;

    if (open === 0 && close === 0 && reduction === 0) return null;

    const floorNote = flooredAtMinimum
      ? `Face drew ${currency(baseDrawdown)}; death benefit reduction capped at the Residual Death Benefit ${currency(close)}.`
      : undefined;

    return (
      <BalanceBreakdownTooltip
        title="Death benefit"
        description="For MoneyGuard, only Face Amount drawdown reduces the death benefit. COB drawdown does not reduce death benefit. The death benefit is floored at the Residual Death Benefit."
        sections={[
          {
            opening: open,
            closing: close,
            lines: [{ label: 'Reduced this month', value: reduction, kind: 'subtract' }],
            note: floorNote,
          },
        ]}
      />
    );
  },

  payoutCellTooltip: ({ month, terms }) => {
    const eligibleDays = month.facts.coverageEligibleDays;
    // Keep the tooltip alive on a fully-ineligible month (waiting period / <2
    // ADL) when room & board is still out-of-pocket — Independent Living books
    // R&B even on non-paying months (cmTwo guards the same way); it's the exact
    // OOP this modeling exists to surface. Otherwise PaidCell shows the
    // zero-row reason natively.
    if (eligibleDays === 0 && (month.facts.roomAndBoardCost ?? 0) === 0) return null;

    const drawnBase = month.deltas.base.depletion;
    const drawnEob = month.deltas.eob.depletion;

    const mode = terms.isIndemnity ? 'indemnity' : 'reimbursement';
    const modeText = terms.isIndemnity
      ? "Pays a fixed cash benefit (the policy's indemnity percentage of the monthly maximum) regardless of actual cost, drawn from the Face pool then COB until the total LTC benefit is exhausted — the claim-wide election never reverts to reimbursement on COB. Cash above the eligible cost banks as a surplus reserve, drained on later shortfall months."
      : 'Reimburses eligible incurred cost, up to the monthly cap.';

    return (
      <TooltipContent className="max-w-xs text-xs">
        <div className="mb-1 font-semibold">Projected payout — {mode}</div>
        <div className="mb-1 text-[10px] text-gray-300">{modeText}</div>
        <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
          {terms.isIndemnity ? (
            // Indemnity is a cash waterfall, not a day-bucket breakdown — see
            // `indemnityPayoutWaterfall`.
            indemnityPayoutWaterfall(month)
          ) : (
            reimbursementPayoutRows({ month })
          )}
        </div>
        <div className="mt-1 text-[10px] text-gray-400">
          Drawn from Face {currency(drawnBase)}
          {drawnEob > 0 ? ` + COB ${currency(drawnEob)}` : ''}
        </div>
      </TooltipContent>
    );
  },

  paidCellExtras: month => {
    const surplusApplied =
      (month.deltas.strategyDeltas as MoneyGuardDeltas | undefined)?.indemnitySurplusApplied ?? 0;
    const isHomeBucket = month.context.careEnvironment === CareEnvironment.home;
    const wpConsumed = isHomeBucket
      ? month.facts.waitingPeriodDaysConsumedHome
      : month.facts.waitingPeriodDaysConsumedFacility;
    const showWaitingPeriod = wpConsumed > 0 && month.deltas.payout.ltcCoveragePaid === 0;

    if (surplusApplied === 0 && !showWaitingPeriod) return null;

    return (
      <>
        {surplusApplied > 0 && (
          <div className="inline-block text-[10px] text-emerald-700">
            +{currency(surplusApplied)} surplus
          </div>
        )}
        {showWaitingPeriod && (
          <div className="mt-0.5 text-[10px] text-amber-700">
            {`${isHomeBucket ? 'Home' : 'Facility'} WP serving ${wpConsumed}d`}
          </div>
        )}
      </>
    );
  },

  // MoneyGuard's monthly cap uses the Face/base inflation rate.
  benefitInflationRate: terms => terms.baseBenefitPoolInflationRate ?? terms.inflationRate,

  copy: {
    poolTooltip: (
      <HeaderTooltip
        description="End-of-month LTC pool balance. MoneyGuard draws from the Face Amount (LTC acceleration) first, then Continuation of Benefits (COB). Inflation can accrue at policy anniversaries while pools remain open."
        formulas={[
          {
            label: 'Closing pool',
            expression: '(opening Face + opening COB) + inflation accrual − drawn this month',
          },
          {
            label: 'Draw order',
            expression: 'Face first; COB engages once the Face pool is exhausted',
          },
        ]}
      />
    ),
    payoutColumnTooltip: (
      <HeaderTooltip
        description="Policy payout this month (reimbursement or indemnity, depending on policy mode), with cumulative dollars paid by the policy through this month in parentheses."
        formulas={[
          {
            label: 'Reimbursement mode',
            expression: 'min(eligible cost, monthly cap, pool remaining)',
          },
          { label: 'Indemnity mode', expression: 'min(monthly cap × indemnity %, pool remaining)' },
        ]}
        notes="Eligible cost excludes waiting-period and <2 ADL days. Indemnity pays regardless of actual cost (the policy's indemnity percentage of the monthly maximum) from both pools (Face then COB) until the total LTC benefit is exhausted — it does not revert to reimbursement on COB."
      />
    ),
    costTooltip: (
      <HeaderTooltip
        description="Projected total monthly care cost (with inflated per-day rate beneath). The daily rate comes from the care plan's phase × environment unit cost."
        formulas={[
          {
            label: 'Daily cost',
            expression: (
              <>
                today's daily unit cost ×{' '}
                <GrowthFactor
                  rate="care-cost inflation"
                  exponent="years from today"
                />
              </>
            ),
          },
          { label: 'Monthly cost', expression: 'daily cost × days in month' },
        ]}
        notes={PROJECTED_COST_NOTE}
      />
    ),
    benefitAmountTooltip: (
      <HeaderTooltip
        description="Policy monthly benefit maximum for this care environment and policy year, with the daily equivalent beneath. MoneyGuard's daily benefit is set per care environment and inflates on the Face Amount rate."
        formulas={[
          {
            label: 'Monthly cap',
            expression: (
              <>
                at-issue daily for environment × days in month ×{' '}
                <GrowthFactor
                  rate="Face inflation rate"
                  exponent="policy year − 1"
                />
              </>
            ),
          },
        ]}
        notes="Face inflation capped at the configured cap year if specified."
      />
    ),
    poolLimitTooltip: (
      <HeaderTooltip
        description="Inflated Face + COB pool sizes at this policy year if no benefits were used. Pools keep growing on each future anniversary until any inflation cap is reached. Distinct from Pool remaining, which is the actual balance after payouts."
        formulas={[
          {
            label: 'Face Amount',
            expression: (
              <>
                Face at issue ×{' '}
                <GrowthFactor
                  rate="Face inflation rate"
                  exponent="policy year − 1"
                />
              </>
            ),
          },
          {
            label: 'COB',
            expression: (
              <>
                COB at issue ×{' '}
                <GrowthFactor
                  rate="COB inflation rate"
                  exponent="policy year − 1"
                />
              </>
            ),
          },
          { label: 'Total', expression: 'Face value + COB value' },
        ]}
        notes="A 0% inflation rider on a pool leaves that pool flat. Once a pool's inflation cap is reached, its value is a true ceiling."
      />
    ),
    drawnTooltip: (
      <HeaderTooltip
        description="Cumulative reduction of the Face + COB pools through this month — any utilization before the projection plus this projection's drawdown."
        formulas={[
          { label: 'Drawn', expression: 'prior utilization + Σ (Face depletion + COB depletion)' },
          {
            label: 'Identity',
            expression: 'Pool limit − Drawn = Pool remaining (while pool is open)',
          },
        ]}
      />
    ),
    deathBenefitTooltip: (
      <HeaderTooltip
        description="End-of-month death benefit, with this month's change beneath. MoneyGuard depletes the death benefit dollar-for-dollar by Face Amount drawdown only; COB drawdown does not reduce the death benefit."
        formulas={[
          { label: 'Death benefit', expression: 'at-issue Face Amount − Σ (Face drawdown)' },
          { label: 'Floor', expression: 'max(above, Residual Death Benefit)' },
        ]}
      />
    ),
    benefitInflationCompoundLabel: 'Compound growth',
    benefitInflationCapNote: capYear => `Inflation capped at year ${capYear}`,
  },
};
